import { useState, useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import { REPORT_LINKS } from "../../../../utils/dashboardLinks";
import { type SearchQueryPerformanceV4Row } from "../../../../modules/sqpr/api/v4SearchQueryPerformanceReportByQuery";
import { useQuery } from "@tanstack/react-query";
import { createColumnBuilder } from "../../../../components/table/columnBuilder";
import { css } from "../../../../../styled-system/css";
import KeywordCell from "../../../../components/custom/category/advertisements/KeywordCell";
import moment from "moment";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { idsToVisibilityState } from "../../../../components/table/utils";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useGetV4SearchQueryPerformanceTotals } from "../../../../modules/sqpr/api/v4SearchQueryPerformanceReportTotals";
import SearchQueryPerformanceDashboardTable from "../../../../modules/sqpr/SearchQueryPerformanceDashboardTable";
import { Flex } from "../../../../../styled-system/jsx";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Button } from "../../../../components/core/basic/Button";
import { Modal } from "../../../../components/core/basic/Modal";
import { ProductSelector } from "../../../../components/core/blocks/ProductSelector/ProductSelector";
import { useGetV4SearchQueryPerformanceByDate } from "../../../../modules/sqpr/api/v4SearchQueryPerformanceByDate";
import { FaCircleInfo } from "react-icons/fa6";
import { useSqprIngestionStatus } from "../../../../modules/workflow-progress-v2/hooks/useSqprIngestionStatus";

const columnHelper = createColumnBuilder<SearchQueryPerformanceV4Row>();

const SqprImportingNotice = () => {
  const { t } = useTranslation(["sqpr"]);
  const { importing, percent } = useSqprIngestionStatus();

  if (!importing || percent == null) return null;

  return (
    <Flex
      alignItems="center"
      gap="small"
      marginBottom="medium"
      paddingX="medium"
      paddingY="small"
      borderRadius="4px"
      backgroundColor="royalPurple/8"
      border="1px solid {colors.royalPurple/15}"
      fontSize="small"
      color="text.heading"
    >
      <FaCircleInfo size={14} className={css({ color: "royalPurple", flexShrink: 0 })} />
      {t(($) => $.sqpr["Search query data is still importing ({percent}%) — recent weeks may be incomplete."], {
        percent,
      })}
    </Flex>
  );
};

const SQPRReportBody = () => {
  const { t } = useTranslation(["sqpr", "common"]);
  const [selectedAsins, setSelectedAsins] = useState<{ product_id: string; asin: string; sku: string | null }[]>([]);
  const [tempSelectedAsins, setTempSelectedAsins] = useState<
    { product_id: string; asin: string; sku: string | null }[]
  >([]);
  const [showAsinFilter, setShowAsinFilter] = useState(false);

  const navigate = useNavigate();

  const { group, setGroup, start, end } = useDates();
  const longDate = useDateFormatter("long");

  if (group !== "week") {
    setGroup("week");
  }

  const standardMetricIds = useStandardEntityMetrics("searchQueryReport");

  const columns = useMemo(
    () => [
      columnHelper.accessor("search_query", {
        id: "search_query",
        header: t(($) => $.sqpr["Search Query"]),
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: ({ getValue }) => {
          return (
            <div className={css({ paddingLeft: "small" })}>
              <KeywordCell value={getValue()} columnWidth={300} />
            </div>
          );
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
    ],
    [standardMetricIds, t],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "search_query",
    "search_query_score",
    "search_query_volume",
    "impressions_total",
    "impressions_brand_or_asin",
    "clicks_total",
    "cart_adds_brand_or_asin",
    "clicks_brand_or_asin",
    "cv_total",
    "cv_brand_or_asin",
    "cv_difference",
    "purchases_total",
    "purchases_brand_or_asin",
    "purchases_brand_or_asin_share",
    "purchases_total_rate",
    "clicks_keyword",
    "sales_keyword",
    "cost_keyword",
    "acos_keyword",
    "conversions_keyword",
    "conversion_rate_keyword",
    "cpc_keyword",
  ];

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["search_query"],
      end: [],
    },
    sorting: [{ id: "search_query_volume", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `searchQueryPerformanceReport`,
    initialState: initialStateConfig,
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);

  const filters = useMemo(() => {
    const asinFilter =
      selectedAsins.length ?
        [
          {
            id: "asins",
            value: { op: "anyof", value: selectedAsins.map((asin) => asin.asin) },
          },
        ]
      : [];

    return [...state.columnFilters, ...asinFilter];
  }, [state.columnFilters, selectedAsins]);

  const byDateOptionsGetter = useGetV4SearchQueryPerformanceByDate();
  const searchQueryPerformanceByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters,
      search: debounceSearch,
    }),
  );

  const totalOptionsGetter = useGetV4SearchQueryPerformanceTotals();
  const searchQueryPerformanceTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters,
      search: debounceSearch,
    }),
  );

  const metrics = useMemo(() => {
    return standardMetricIds.map((metricId) => getMetricSpec(metricId));
  }, [standardMetricIds]);

  return (
    <PageContainer withBreadcrumb>
      <SqprImportingNotice />
      <MetricsCard
        id={"searchQueryPerformanceReport"}
        title={t(($) => $.sqpr["Performance Overview"])}
        totals={{
          data: searchQueryPerformanceTotalsReportQuery.data?.data,
          loading:
            searchQueryPerformanceTotalsReportQuery.isFetching &&
            !searchQueryPerformanceTotalsReportQuery.isPlaceholderData,
        }}
        timeline={{
          data:
            searchQueryPerformanceByDateReportQuery.data?.data ?
              searchQueryPerformanceByDateReportQuery.data.data.map((row) => ({
                ...row,
                start_date: row.reporting_date,
                name: row.reporting_date,
              }))
            : [],
          loading:
            searchQueryPerformanceByDateReportQuery.isFetching &&
            !searchQueryPerformanceByDateReportQuery.isPlaceholderData,
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["search_query_score", "search_query_volume", "impressions_total", "clicks_total"]}
        group={"day"}
        showAnnotations={false}
        byline={
          <>
            {longDate.format(moment(start).toDate())}
            {" - "}
            {longDate.format(moment(end).endOf("week").toDate())}
          </>
        }
        context={"searchQueryReport"}
        inlineStatus={
          <Flex className={css({ gap: "small", alignItems: "center", paddingX: "large", whiteSpace: "nowrap" })}>
            <Button
              variant={"link"}
              className={css({ paddingStart: "large", fontSize: "xl" })}
              onPress={() => void navigate({ to: "/user/searchqueryperformance" })}
            >
              {t(($) => $.sqpr["View Uploaded Reports"])}
            </Button>
          </Flex>
        }
      >
        <Modal
          isOpen={showAsinFilter}
          onOpenChange={(isOpen) => !isOpen && setShowAsinFilter(false)}
          title={t(($) => $.sqpr["Select ASINs to Filter Stats"])}
          css={{ width: "55vw", minHeight: "600px", padding: "large", overflowY: "auto" }}
        >
          <ProductSelector
            selectedRowIds={tempSelectedAsins.map((asin) => `${asin.product_id}:${asin.asin}:${asin.sku}`)}
            onSelectionChange={(selectedProducts: string[]) => {
              setTempSelectedAsins(
                selectedProducts.map((productKey) => {
                  const [productId, asin, sku] = productKey.split(":");
                  return {
                    product_id: String(productId),
                    asin: String(asin),
                    sku: String(sku),
                  };
                }),
              );
            }}
            context={`searchQueryPerformanceDashboardProductSelector`}
            enableRowSelection={true}
            enableSelectAll={false}
            rowIdAccessor={"product_key"}
          />
          <Flex gap={"large"} justifyContent={"center"} marginTop={"auto"}>
            <Button
              variant="secondary"
              onPress={() => {
                setShowAsinFilter(false);
              }}
            >
              {t(($) => $.common.actions["Cancel"])}
            </Button>
            <Button
              onPress={() => {
                setSelectedAsins(tempSelectedAsins);
                setShowAsinFilter(false);
              }}
            >
              {t(($) => $.common.actions["Confirm"])}
            </Button>
          </Flex>
        </Modal>

        <SearchQueryPerformanceDashboardTable
          state={state}
          initialState={initialState}
          setters={setters}
          setShowAsinFilter={(value) => {
            if (value) {
              setTempSelectedAsins([...selectedAsins]);
            }
            setShowAsinFilter(value);
          }}
          selectedAsins={selectedAsins}
          setSelectedAsins={setSelectedAsins}
          showDownloadButton
          downloadButtonText={t(($) => $.sqpr["Download Report"])}
          downloadFileName={"sqpr-report.xlsx"}
          columns={columns}
        />
      </MetricsCard>
    </PageContainer>
  );
};

function SQPRReportV2() {
  const { t } = useTranslation(["sqpr", "common"]);
  const breadCrumbItems = [
    {
      name: t(($) => $.sqpr["Search Query Performance"]),
      href: "/user/report/sqpr_report",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.common.sidebar["Reports"]),
    },
  ];

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} hideCompare={true} hideGroupBy={true} />
      <SQPRReportBody />
    </DatesProvider>
  );
}

export const Route = createFileRoute("/_authenticated/user/report/sqpr_report")({
  component: SQPRReportV2,
  staticData: { application: "Reports" },
});
